/**
 * Default chunk size for file uploads (4MB)
 */
export const CHUNK_SIZE = 4 * 1024 * 1024;

/**
 * Calculate SHA-256 hash of a file or buffer using Web Crypto.
 * Returns a lowercase hex string. The browser implementation is native and
 * runs off the main thread, so no manual chunking is needed for medical
 * volume sizes.
 */
export async function calculateSHA256(input: File | ArrayBuffer): Promise<string> {
    const buffer = input instanceof ArrayBuffer ? input : await input.arrayBuffer();
    const digest = await crypto.subtle.digest('SHA-256', buffer);
    return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Slice a file into chunks for upload.
 * Returns an array of Blob chunks.
 */
export function sliceFileChunks(file: File, chunkSize: number = CHUNK_SIZE): Blob[] {
    const chunks: Blob[] = [];
    let offset = 0;

    while (offset < file.size) {
        const end = Math.min(offset + chunkSize, file.size);
        chunks.push(file.slice(offset, end));
        offset = end;
    }

    return chunks;
}

/**
 * Get the total number of chunks for a file.
 */
export function getTotalChunks(fileSize: number, chunkSize: number = CHUNK_SIZE): number {
    return Math.ceil(fileSize / chunkSize);
}

/**
 * Convert a Blob to ArrayBuffer.
 */
export async function blobToArrayBuffer(blob: Blob): Promise<ArrayBuffer> {
    return blob.arrayBuffer();
}

/**
 * File type to accepted extensions mapping based on data slot types.
 */
export const DATA_TYPE_EXTENSIONS: Record<string, string[]> = {
    VOLUME: ['.dcm', '.mha', ''],
    MESH: ['.stl', '.obj', '.vtk'],
    LABEL_MAP: ['.nii', '.nii.gz', '.nrrd'],
    JSON: ['.json'],
    PDF: ['.pdf'],
};

/**
 * Get accepted file extensions for a data type.
 */
export function getAcceptedExtensions(dataType: string): string {
    const extensions = DATA_TYPE_EXTENSIONS[dataType] || [];
    return extensions.join(',');
}

/**
 * Check if a file extension is valid for a given data type.
 */
export function isValidFileType(filename: string, dataType: string): boolean {
    const extensions = DATA_TYPE_EXTENSIONS[dataType] || [];
    const lowerFilename = filename.toLowerCase();
    return extensions.some((ext) => lowerFilename.endsWith(ext));
}

/**
 * Get content type for a file based on its extension.
 */
export function getContentType(filename: string): string {
    const lowerFilename = filename.toLowerCase();

    if (lowerFilename.endsWith('.nii.gz')) return 'application/gzip';
    if (lowerFilename.endsWith('.nii')) return 'application/octet-stream';
    if (lowerFilename.endsWith('.nrrd')) return 'application/octet-stream';
    if (lowerFilename.endsWith('.mha')) return 'application/octet-stream';
    if (lowerFilename.endsWith('.dcm')) return 'application/dicom';
    if (lowerFilename.endsWith('.stl')) return 'model/stl';
    if (lowerFilename.endsWith('.obj')) return 'model/obj';
    if (lowerFilename.endsWith('.vtk')) return 'application/octet-stream';
    if (lowerFilename.endsWith('.json')) return 'application/json';
    if (lowerFilename.endsWith('.pdf')) return 'application/pdf';

    return 'application/octet-stream';
}

/**
 * Format file size for display.
 */
export function formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 B';

    const units = ['B', 'KB', 'MB', 'GB'];
    const k = 1024;
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${units[i]}`;
}
